//core
import React, { useCallback, useEffect, useRef, useState } from "react";
import arePropsEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Box, IconButton, Menu, Tooltip, Typography, useMediaQuery } from "@mui/material";

//icons
import PreviewIcon from "@mui/icons-material/Preview";

//styles
import { useStyles } from "./styles";

//routes
import routes from "../../../../../navigation/routes";

//services
import { affTrackersService } from "../../../../../services/admin/affTrackers.service";

//hooks
import { useUser } from "../../../../../store/common/user/useUser";
import { useConditions } from "../../../../../store/admin/conditions/useConditions";
import { useUI } from "../../../../../store/common/ui/useUI";
import { usePermissions } from "../../../../../store/common/configuration/usePermissions";

//selectors
import {
  selectConditionConstantsData,
  selectViewConditionData,
  selectViewConditionError,
} from "../../../../../store/admin/conditions/selectors";

//components
import DealDetailsView from "../../../../admin/Deals/components/DealDetailsView";
import CircularLoader from "../../../../UI/CircularLoader";
import MuiModal from "../../../../UI/MuiModal";

interface IClickableTrackerId {
  value: string | number | null;
}

let activePreviewId: symbol | null = null;

const ClickableTrackerId = ({
  value = '',
}: IClickableTrackerId) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { user } = useUser();
  const { setGeneralSnackbar } = useUI();
  const { hasPermissions } = usePermissions();
  const mobile = useMediaQuery("(max-width:767px)");
  const location = useLocation();

  const isAdmin = user.user.namespace === "admin";
  const isAssignmentsPage = location.pathname.includes('/admin/deals/assignments/');

  const hasPreview = hasPermissions(["api2.admin.conditionscontroller.preview"]);
  const hasShowProfile = hasPermissions(["api3.admin.affiliatescontroller.profile"]);
  const hasShowAdmTrackerList = hasPermissions(["api3.admin.trackerslistcontroller.show"]);
  const hasShowAffTrackerList = hasPermissions(["api3.affiliate.trackerscontroller.show"]);
  const hasShowAffTrackerListDetails = hasPermissions(["api2.affiliate.trackerscontroller.show"]);

  const canAdminNavigate = hasShowProfile && hasShowAdmTrackerList;
  const canAffNavigate = hasShowAffTrackerList && hasShowAffTrackerListDetails;
  const canNavigateToTracker = isAdmin ? canAdminNavigate : canAffNavigate;

  const {
    getConstants,
    getViewCondition,
    clearViewCondition,
  } = useConditions();

  const constantsData = useSelector(selectConditionConstantsData);
  const conditionData = useSelector(selectViewConditionData);
  const conditionError = useSelector(selectViewConditionError);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentEl, setCurrentEl] = useState<null | HTMLElement>(null);
  const [previewDisable, setPreviewDisable] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const requestIdRef = useRef<symbol | null>(null);

  const open = Boolean(anchorEl);

  const isActive = () => requestIdRef.current !== null && requestIdRef.current === activePreviewId;

  useEffect(() => {
    if (!isActive()) return;

    if ((!!constantsData && !!conditionData) || !!conditionError) {
      if (mobile) {
        setShowModal(true);
      } else {
        setAnchorEl(currentEl);
      }
    }
  }, [constantsData, conditionData, conditionError, currentEl, mobile]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setCurrentEl(null);
    setShowModal(false);
    clearViewCondition();
    setPreviewDisable(false);
    if (isActive()) {
      activePreviewId = null;
    }
    requestIdRef.current = null;
  }, [clearViewCondition]);

  const handlePreviewClick = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setCurrentEl(event.currentTarget);
    setPreviewDisable(true);

    const requestId = Symbol();
    requestIdRef.current = requestId;
    activePreviewId = requestId;

    try {
      const response = await affTrackersService.getTrackerById(`${value}`);

      if (activePreviewId !== requestId) {
        setPreviewDisable(false);
        setCurrentEl(null);
        return;
      }

      if (!response.ok) {
        setGeneralSnackbar({
          open: true,
          type: 'warning',
          message: t("common.messages.access_error"),
        });
        setPreviewDisable(false);
        setCurrentEl(null);
        activePreviewId = null;
        requestIdRef.current = null;
        return;
      }

      const data = await response.json();
      const conditionId = data?.condition?.id;

      if (conditionId) {
        getConstants();
        getViewCondition(conditionId);
      } else {
        setPreviewDisable(false);
        setCurrentEl(null);
        activePreviewId = null;
        requestIdRef.current = null;
      }
    } catch {
      setPreviewDisable(false);
      setCurrentEl(null);
      if (activePreviewId === requestId) {
        activePreviewId = null;
      }
      requestIdRef.current = null;
    }
  }, [value, getConstants, getViewCondition, setGeneralSnackbar, t]);

  const handleIdClick = async () => {
    if (!value) return;

    if (isAdmin) {
      const newWindow = window.open('about:blank', '_blank');

      try {
        const response = await affTrackersService.getTrackerById(`${value}`);

        if (!response.ok) {
          if (newWindow) {
            newWindow.close();
          }
          setGeneralSnackbar({
            open: true,
            type: 'warning',
            message: t("common.messages.access_error"),
          });
          return;
        }

        const data = await response.json();

        let url: string;

        if (isAssignmentsPage) {
          url = routes.admin.partners.account.trackers.conditions
            .replace(':affId', `${data?.tracker?.affiliate_id || ''}`)
            .replace(':trackerId', `${value}`);
        } else {
          url = routes.admin.partners.account.trackers.details
            .replace(':affId', `${data?.tracker?.affiliate_id || ''}`)
            .replace(':trackerId', `${value}`);
        }

        if (newWindow) {
          newWindow.location.href = url;
        }
      } catch (error: any) {
        if (newWindow) {
          newWindow.close();
        }
        console.error(error.message);
      }
    } else {
      const url = routes.affiliate.trackers.details.id.replace(':trackerId', `${value}`);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {value ? (
        <Box className={styles.wrapper}>
          {canNavigateToTracker ? (
            <span className={styles.text} onClick={handleIdClick}>
              {value}
            </span>
          ) : (
            <span>{value}</span>
          )}

          {isAdmin && hasPreview && (
            <Tooltip title={t("admin.deals.summary.dealPreview")}>
              <IconButton
                onClick={handlePreviewClick}
                size="small"
                className={styles.previewButton}
                disabled={previewDisable}
              >
                <PreviewIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        '—'
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box className={styles.menu}>
          {(!!constantsData && !!conditionData) || !!conditionError ? (
            <DealDetailsView
              constantsData={constantsData}
              conditionData={conditionData}
              conditionError={conditionError}
            />
          ) : (
            <CircularLoader size={32} />
          )}
        </Box>
      </Menu>

      <MuiModal
        width={500}
        open={showModal}
        closeOnOutside={false}
        setToggleModal={(state: boolean) => {
          if (!state) {
            handleClose();
          }
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("admin.deals.summary.dealPreview")}
        </Typography>
        {(!!constantsData && !!conditionData) || !!conditionError ? (
          <DealDetailsView
            constantsData={constantsData}
            conditionData={conditionData}
            conditionError={conditionError}
          />
        ) : (
          <CircularLoader size={32} />
        )}
      </MuiModal>
    </>
  );
};

export default React.memo(ClickableTrackerId, arePropsEqual);
